import { ActionType } from 'api/betslipAnalytics.api';
import cn from 'classnames';
import { motion } from 'framer-motion';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, RouteComponentProps, useLocation, withRouter } from 'react-router-dom';
import { LiveEventOutcomeStatus, outcomeIdsToExclude } from 'shared/constants/common';
import { useBetslipAnalytics } from 'shared/hooks/useBetslipAnalytics';
import { EventType, PlaceBet, State } from 'shared/interface';
import { sortedIds } from 'shared/util/sortedIds';
import { isMobile, OUTCOME_NAME_DRAW } from 'shared/util/utility';
import { OutcomeOddsUpdateState } from '../../../../features/coupon/interface';
import './styles.scss';

interface OddsButtonsProps extends RouteComponentProps {
	data: any;
	returnCheck?: (id: number) => boolean | null;
	onClickOdds: (item: PlaceBet, e: React.MouseEvent) => void;
	eventUrl?: any;
	noMaxHeightOdds?: boolean;
	animateOdds?: boolean;
	expandOdds?: boolean;
	eventViewOdds?: boolean;
}

const OddsButtons = ({
	eventUrl,
	data,
	returnCheck = () => null,
	onClickOdds,
	expandOdds,
	noMaxHeightOdds = false,
	animateOdds = true,
	eventViewOdds,
}: OddsButtonsProps) => {
	const location = useLocation();
	const { addAction } = useBetslipAnalytics();
	const lock = isEmpty(data.outcomes);
	const oddsRef = useRef<HTMLDivElement>(null);
	const defaultOddsHeight = isMobile() ? 44 : 48;
	const [maxOddsHeight, setMaxOddsHeight] = useState(defaultOddsHeight);
	const { t } = useTranslation();
	const bets = useSelector((state: State) => state.coupon.bets);
	const allBets = [...bets.blocks, ...bets.outcomes];
	const numberOfOutcomes = data?.outcomes?.length;

	const checkOddIsInBet = (outcomeId: string) => allBets.flat().some((bet) => bet.outcomeId === outcomeId);

	if (sortedIds.includes(data.gameType)) {
		data.outcomes = data.outcomes.sort((a: any, b: any) => a.outcomeOdds - b.outcomeOdds);
	}

	const isSisOutcomeInactive = (status: number) => status === LiveEventOutcomeStatus.AWAITING_APPROVAL;

	const isOutcomeLocked = ({ outcomeOdds, status }: any) => outcomeOdds === 1 || isSisOutcomeInactive(status);

	const handleAddBet = (transformedData: any, e: React.MouseEvent, action_type: ActionType) => {
		e.preventDefault();
		addAction(transformedData, location.pathname, action_type);
		if (!isOutcomeLocked(transformedData)) {
			onClickOdds(transformedData, e);
		}
	};

	const renderName = (outcomeName: string) => (
		<div className="oddsButton__name">{outcomeName && outcomeName === OUTCOME_NAME_DRAW ? t('draw') : outcomeName}</div>
	);

	useEffect(() => {
		setMaxOddsHeight(oddsRef.current?.getBoundingClientRect().height as number);
	}, []);

	return (
		<motion.div
			animate={
				noMaxHeightOdds
					? { overflow: expandOdds ? 'auto' : 'hidden' }
					: { maxHeight: expandOdds ? maxOddsHeight : defaultOddsHeight, overflow: expandOdds ? 'auto' : 'hidden' }
			}
			transition={{ duration: 0.3 }}
			className={cn('odds', { 'odds--expanded': expandOdds })}
		>
			{data && !isEmpty(data) && data?.outcomes && !isEmpty(data.outcomes) && (
				<div
					onClick={(e) => e.stopPropagation()}
					ref={oddsRef}
					className={cn(
						'odds__wrapper',
						{ 'odds__wrapper--triple': numberOfOutcomes && numberOfOutcomes >= 3 },
						{ 'odds__wrapper--double': numberOfOutcomes && numberOfOutcomes === 2 },
					)}
				>
					{data.outcomes.map((outcome: any) => {
						const isInBetslip = checkOddIsInBet(outcome.outcomeId);
						const transformedData = {
							outcomeId: outcome.outcomeId,
							outcomeOdds: outcome.outcomeOdds,
							outcomeName: outcome.outcomeName,
							outcomeUpdateState: returnCheck
								? returnCheck(outcome.outcomeId) !== null
									? returnCheck(outcome.outcomeId)
										? OutcomeOddsUpdateState.ODDS_INCREASED
										: OutcomeOddsUpdateState.ODDS_DECREASED
									: OutcomeOddsUpdateState.ODDS_NOT_CHANGED
								: OutcomeOddsUpdateState.ODDS_NOT_CHANGED,
							sportId: data.sportId,
							eventStart: data.eventStart,
							eventId: data.eventId,
							eventType: data.eventType,
							gameName: data.gameName,
							gameType: data.gameType,
							categoryId: data.categoryId,
							gameId: data.gameId,
							eventName: data.eventName,
							league: `${data.sportName} / ${data.parentName} / ${data.categoryName}`,
							selectedBet: true,
							banker: false,
							isLive: data.eventType === EventType.LIVE,
							status: outcome.status,
							outcomesLength: data.outcomesLength ? data.outcomesLength : data.outcomes.length,
						};

						return (
							(transformedData.outcomeOdds !== 1 || !outcomeIdsToExclude.includes(transformedData.gameType)) && (
								<div
									key={outcome.outcomeId}
									className={cn('oddsButton', {
										'oddsButton--active': isInBetslip,
										oddsUp: animateOdds && returnCheck(transformedData.outcomeId) === true,
										oddsDown: animateOdds && returnCheck(transformedData.outcomeId) === false,
									})}
									data-cy="outcome-button"
									onClick={(e) => handleAddBet(transformedData, e, isInBetslip ? ActionType.REMOVE : ActionType.ADD)}
								>
									{outcome.outcomeName && !isOutcomeLocked(transformedData) && renderName(outcome.outcomeName)}
									{!isOutcomeLocked(transformedData) && (
										<div className="oddsButton__odds">{transformedData.outcomeOdds?.toFixed(2)}</div>
									)}
									{isOutcomeLocked(transformedData) && (
										<div className="oddsButton--disabled">{eventViewOdds ? t('noOdds') : t('goToEvent')}</div>
									)}
								</div>
							)
						);
					})}
				</div>
			)}

			{data.gamesCount === 0 && (
				<Link to={eventUrl} className="oddsButton oddsButton--disabled">
					{t('goToEvent')}
				</Link>
			)}

			{lock && !data.isDetailedView && data.gamesCount > 0 && (
				<Link to={eventUrl} className="oddsButton oddsButton--disabled">
					{t('goToEvent')}
				</Link>
			)}
		</motion.div>
	);
};

export default withRouter(OddsButtons);
